// src/utils/visitDate.js
//
// Plausibility guard for service-visit timestamps. Bug IyEyoBbQu8r8S3kwRDNG: some
// auto/scheduled writes persisted a date-less time as the proto/epoch-zero placeholder
// `0001-01-01T..` — a non-null but absurd value. Treating ANY non-null scheduledStart as
// a "booked visit" (a) hid real faults from the Site Monitoring truck-roll list and
// (b) rendered "Scheduled 01/01/0001" in the UI. Use these helpers anywhere a visit date
// is read for "is a truck booked?" or written. (The backend mirror lives in
// functions/monitoring/lifetime/remediationState.js — keep the window in sync.)

const FLOOR_MS = Date.UTC(2015, 0, 1) // before any real GSE service case
const CEIL_MS = Date.UTC(2100, 0, 1) // guards year-9999 / overflow placeholders

// Coerce a Firestore Timestamp / Date / ISO string / epoch-ms to epoch-ms, or null.
export function toMs(v) {
  if (v == null) return null
  if (typeof v.toMillis === 'function') return v.toMillis()
  if (typeof v.toDate === 'function') {
    try {
      return v.toDate().getTime()
    } catch {
      return null
    }
  }
  if (v instanceof Date) {
    const t = v.getTime()
    return Number.isNaN(t) ? null : t
  }
  if (typeof v === 'string') {
    const t = Date.parse(v)
    return Number.isNaN(t) ? null : t
  }
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  return null
}

// Epoch-ms iff the value is a plausible real timestamp; else null (Invalid / 0001-01-01 / far-future).
export function plausibleMs(v) {
  const ms = toMs(v)
  if (ms == null || ms < FLOOR_MS || ms > CEIL_MS) return null
  return ms
}

// Does this visit object carry a plausibly-real scheduled date (i.e. a truck is booked)?
export function hasPlausibleVisit(visit) {
  return plausibleMs(visit?.scheduledStart) != null
}

// Pass a plausible date value straight through (preserving its type for the write),
// else null — for sanitizing visit dates at the write boundary.
export function sanitizeVisitDate(v) {
  return plausibleMs(v) != null ? v : null
}
